import { getScopedStorageKey } from "@/lib/sync/storage-scope";
import { buildPendingSyncDebugState } from "@/lib/debug/pending-sync-debug";
import { readClientNavTransactions } from "@/lib/navigation/client-navigation-store";
import { loadWorkoutFromStore, type StoredWorkoutData } from "@/lib/sync/workout-store";
import {
  readReportDebugHistoryEditNavigationSnapshot,
  readReportDebugHistoryEditScrollSnapshot,
} from "@/lib/debug/reportdebug-route-context";
import { NAV_DEBUG_FLAGS_CHANGED_EVENT, readReportDebugFlag } from "@/lib/navigation/debug-flags";
import {
  collectReportDebugUiCardSnapshots,
  readReportDebugUiCardSnapshots,
  readReportDebugUiCardTrail,
} from "@/lib/debug/ui-card-debug";

const CLIENT_DEBUG_ACTIONS_KEY = "clientDebugActions";
const CLIENT_DEBUG_COLLECTION_ENABLED_KEY = "clientDebugCollectionEnabled";
const CLIENT_KEYBOARD_DEBUG_SNAPSHOT_KEY = "clientReportDebugKeyboardSnapshot";
export const CLIENT_DEBUG_ACTION_EVENT = "client-debug-action";
const CLIENT_DEBUG_ACTIONS_MAX = 200;
const SNAPSHOT_RECENT_ACTIONS_MAX = 50;

const SNAPSHOT_ROUTE_HISTORY_MAX = 10;
const SNAPSHOT_TRAIL_MAX = 20;
const SNAPSHOT_START_WORKOUT_TRACE_MAX = 20;
const SNAPSHOT_DETAILS_STRING_MAX = 400;
const SNAPSHOT_DETAILS_DEPTH_MAX = 3;
const REPORT_DEBUG_WORKOUT_STATE_KEY = "clientReportDebugWorkoutState";
const REPORT_DEBUG_WORKOUT_TRAIL_KEY = "clientReportDebugWorkoutTrail";
const REPORT_DEBUG_WORKOUT_TRAIL_MAX = 20;
const REPORT_DEBUG_START_WORKOUT_BREADCRUMB_KEY = "clientReportDebugStartWorkoutBreadcrumb";
const REPORT_DEBUG_ONBOARDING_TIP_STATE_KEY = "clientReportDebugOnboardingTipState";
const REPORT_DEBUG_PULL_TO_REFRESH_STATE_KEY = "clientReportDebugPullToRefreshState";
const REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_KEY = "clientReportDebugPullToRefreshTrail";
const REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_MAX = 20;
const REPORT_DEBUG_HISTORY_QUERY_STATE_KEY = "clientReportDebugHistoryQueryState";
const REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_KEY = "clientReportDebugNutritionCustomFoodTrail";
const REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_MAX = 20;
const REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_KEY = "clientReportDebugNutritionSearchTrail";
const REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_MAX = 30;
const REPORT_DEBUG_NUTRITION_LOG_TRAIL_KEY = "clientReportDebugNutritionLogTrail";
const REPORT_DEBUG_NUTRITION_LOG_TRAIL_MAX = 60;
const REPORT_DEBUG_DAILY_ROUTINE_TRAIL_KEY = "clientReportDebugDailyRoutineTrail";
const REPORT_DEBUG_DAILY_ROUTINE_TRAIL_MAX = 60;
const REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_KEY = "clientReportDebugOnboardingEventTrail";
const REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_MAX = 30;
const REPORT_DEBUG_SLIDER_TRAIL_KEY = "clientReportDebugSliderTrail";
const REPORT_DEBUG_SLIDER_TRAIL_MAX = 40;
const REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_KEY = "clientReportDebugProgramSelectorDragTrail";
const REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_MAX = 80;
const REPORT_DEBUG_GALLERY_DATA_STATE_KEY = "clientReportDebugGalleryDataState";
const REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_KEY = "clientReportDebugGalleryCapturedDateTrail";
const REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_MAX = 30;
const REPORT_DEBUG_GALLERY_UPLOAD_PROCESSING_STATE_KEY = "clientReportDebugGalleryUploadProcessingState";
const REPORT_DEBUG_VIDEO_UPLOAD_STATE_KEY = "clientReportDebugVideoUploadState";
const REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_KEY = "clientReportDebugMediaUploadPerfTrail";
const REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_MAX = 80;
const REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_KEY = "clientReportDebugGalleryPreviewPerfTrail";
const REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_MAX = 80;
const REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_STATE_KEY = "clientReportDebugDashboardOverviewChartState";
const REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_KEY = "clientReportDebugDashboardOverviewChartTrail";
const REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_MAX = 12;
const REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_STATE_KEY = "clientReportDebugDashboardDailyCaloriesState";
const REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_KEY = "clientReportDebugDashboardDailyCaloriesTrail";
const REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_MAX = 12;
const REPORT_DEBUG_GOALS_EDIT_TRAIL_KEY = "clientReportDebugGoalsEditTrail";
const REPORT_DEBUG_GOALS_EDIT_TRAIL_MAX = 30;

export type ClientDebugAction = {
  atIso: string;
  source: string;
  event: string;
  details?: Record<string, unknown>;
};

export function readClientDebugCollectionEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CLIENT_DEBUG_COLLECTION_ENABLED_KEY) === "1";
}

export function shouldCollectClientDebugActions(): boolean {
  if (typeof window === "undefined") return false;
  return readClientDebugCollectionEnabled() || readReportDebugFlag();
}

export function writeClientDebugCollectionEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  if (enabled) {
    localStorage.setItem(CLIENT_DEBUG_COLLECTION_ENABLED_KEY, "1");
    window.dispatchEvent(new CustomEvent(NAV_DEBUG_FLAGS_CHANGED_EVENT));
    return;
  }

  localStorage.removeItem(CLIENT_DEBUG_COLLECTION_ENABLED_KEY);
  localStorage.removeItem(CLIENT_DEBUG_ACTIONS_KEY);
  window.dispatchEvent(new CustomEvent(NAV_DEBUG_FLAGS_CHANGED_EVENT));
}

type BuildDebugSnapshotInput = {
  userNote?: string;
  routeHistory?: unknown;
  navTrail?: unknown;
  historyTrail?: unknown;
  lifecycleTrail?: unknown;
  extra?: Record<string, unknown>;
};

type DebugTimelineEntry = {
  source?: unknown;
  event?: unknown;
  atIso?: unknown;
  [key: string]: unknown;
};

type ClientDebugSnapshot = {
  userNote: string;
  snapshotAtIso: string;
  runtime?: string;
  location: Record<string, unknown>;
  env: Record<string, unknown>;
  routeHistory?: unknown[];
  navTrail?: DebugTimelineEntry[];
  historyTrail?: DebugTimelineEntry[];
  lifecycleTrail?: DebugTimelineEntry[];
  clientNavTransactionTrail?: unknown[];
  workoutStoreSummary?: unknown;
  workoutReportDebugState?: unknown;
  workoutReportDebugTrail?: unknown[];
  [key: string]: unknown;
};

export type ReportDebugWorkoutState = {
  snapshotAtIso: string;
  routePath: string;
  sessionId: string;
  programDayId: string | null;
  programDayName: string;
  clientProgramId: string | null;
  activeExerciseIndex: number;
  activeExerciseId: string | null;
  activeExerciseName: string | null;
  currentSetCount: number;
  currentSetRows?: Array<{
    setNumber: number;
    reps: number | null;
    weight: number | null;
    weightUnit: string;
    rpe: number | null;
    isWarmup: boolean;
  }>;
  setInputDrafts: Record<string, string>;
  savedSetNumbers: Record<string, number[]>;
  sessionNotes: string;
};

export type ReportDebugWorkoutTrailEntry = {
  atIso: string;
  source: string;
  event: string;
  routePath: string | null;
  sessionId: string | null;
  programDayId: string | null;
  clientProgramId: string | null;
  details?: Record<string, unknown>;
};

export type ReportDebugStartWorkoutBreadcrumb = {
  capturedAtIso: string;
  source: string;
  sourceComponent: string;
  routePath: string;
  sessionId: string;
  routineId: string;
  clientProgramId: string | null;
  startClickedAtMs: number;
  pushCalledAtMs: number;
};

export type ReportDebugOnboardingTipState = {
  snapshotAtIso: string;
  routePath: string;
  instanceId?: string;
  stepId: string;
  foodId: string | null;
  foodName: string | null;
  open: boolean;
  isReady: boolean;
  isSeen: boolean;
  eligible: boolean;
  dismissedTemporarily: boolean;
  activeStepId: string | null;
  deferredUntilReopen: boolean;
  anchorMounted: boolean;
  sheetReady: boolean;
  tipMounted: boolean;
  persistedFound?: boolean;
  persistedKeys?: string[];
  source?: string;
};

type PullToRefreshState = "idle" | "pulling" | "triggered" | "refreshing";

type PullToRefreshOutcome = "completed" | "failed" | "timeout" | "cancelled" | null;

export type ReportDebugPullToRefreshState = {
  snapshotAtIso: string;
  routePath: string;
  state: PullToRefreshState;
  pullDistance: number;
  refreshOffset: number;
  refreshOffsetTarget: number;
  holdDistance: number | null;
  enabled: boolean;
  refreshEligible: boolean;
  scrollTop: number | null;
  scrollHeight: number | null;
  clientHeight: number | null;
  touchStartX?: number | null;
  touchStartY: number | null;
  latestTouchX?: number | null;
  latestTouchY: number | null;
  latestDeltaX?: number | null;
  latestDeltaY: number | null;
  gestureAxis?: "horizontal" | "vertical" | null;
  gestureDecisionReason?: string | null;
  activeRefreshId: number | null;
  activeRefreshStartedAtIso: string | null;
  activeRefreshElapsedMs: number | null;
  activeRefreshTimeoutAtIso: string | null;
  activeRefreshTimeoutMs: number | null;
  refreshOutcome: PullToRefreshOutcome;
  latestEvent: string | null;
  latestReason: string | null;
  latestRefreshKeys: string[];
};

export type ReportDebugHistoryQueryState = {
  snapshotAtIso: string;
  routePath: string | null;
  queryKey: string[];
  status: string;
  fetchStatus: string;
  isLoading: boolean;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
  isError: boolean;
  errorMessage: string | null;
  sessionsCount: number | null;
  nextCursorPresent: boolean | null;
  exerciseLogVideoCount: number | null;
  cacheHydrated: boolean | null;
  dataUpdatedAt: number | null;
  serverDebug: Record<string, unknown> | null;
};

export type ReportDebugNutritionCustomFoodTrailEntry = {
  atIso: string;
  routePath: string;
  event: "open" | "submit-start" | "submit-success" | "submit-error" | "close";
  entryPoint: "edit" | "barcode" | "manual" | "unknown";
  isEditing: boolean;
  hasBarcode: boolean;
  sharePublicly: boolean | null;
  foodId: string | null;
  foodName: string | null;
  closeReason: "manual-close" | "submit-success" | "unmount" | null;
  error: string | null;
};

export type ReportDebugNutritionSearchTrailEntry = {
  atIso: string;
  routePath: string;
  event:
    | "history-search"
    | "catalog-search-start"
    | "catalog-search-cache-hit"
    | "catalog-search-success"
    | "catalog-search-error"
    | "catalog-search-exception"
    | "catalog-search-aborted"
    | "catalog-search-stale";
  query: string;
  normalizedQuery: string;
  enabled: boolean;
  durationMs: number | null;
  resultCount: number | null;
  cacheHit: boolean | null;
  error: string | null;
  topResults?: Array<{
    id: string;
    name: string;
    source: string;
    brand: string | null;
    searchRank: number | null;
    matchKind: string | null;
  }>;
};

export type ReportDebugNutritionLogTrailEntry = {
  atIso: string;
  routePath: string;
  event:
    | "add-intent"
    | "optimistic-cache-write-start"
    | "optimistic-cache-write-complete"
    | "log-visible-commit"
    | "persist-start"
    | "persist-success"
    | "persist-error"
    | "sync-queued"
    | "invalidate-start"
    | "refetch-settled"
    | "query-observed-with-pending-sync"
    | "query-count-drop-with-pending-sync";
  flowId: string;
  sourceSurface: string;
  targetDate: string | null;
  mealType: string | null;
  activeTab: string | null;
  tempIds?: string[];
  persistedIds?: string[];
  logsBefore?: number | null;
  logsAfter?: number | null;
  dayLogsBefore?: number | null;
  dayLogsAfter?: number | null;
  cacheWriteMs?: number | null;
  intentToOptimisticMs?: number | null;
  optimisticToVisibleMs?: number | null;
  persistMs?: number | null;
  refetchMs?: number | null;
  error?: string | null;
  serverTiming?: unknown;
  serverTimingPresent?: boolean | null;
  serverTimingMissingReason?: string | null;
  actionResultKeys?: string[];
  debugResultKeys?: string[];
  serverTimingKeys?: string[];
  pendingSyncCount?: number | null;
  pendingSyncTypes?: string[];
  pendingAddCalorieLogCount?: number | null;
  pendingAddCalorieLogTempIds?: string[];
  pendingAddCalorieLogTargetDates?: string[];
  pendingAddCalorieLogMealTypes?: string[];
  pendingAddCalorieLogSources?: string[];
  visiblePendingAddCalorieLogTempIds?: string[];
  previousVisiblePendingAddCalorieLogTempIds?: string[];
  droppedPendingAddCalorieLogTempIds?: string[];
  details?: Record<string, unknown>;
};

export type ReportDebugDailyRoutineTrailEntry = {
  atIso: string;
  routePath: string;
  event:
    | "weight-intent"
    | "optimistic-checkin"
    | "optimistic-weight-log"
    | "queue-sync"
    | "sync-start"
    | "sync-deferred"
    | "sync-finish"
    | "sync-error"
    | "weight-persist-start"
    | "weight-persist-success"
    | "weight-persist-error"
    | "checkin-persist-start"
    | "checkin-persist-success"
    | "checkin-persist-error"
    | "streak-invalidate";
  flowId: string;
  step: string | null;
  tempId?: string | null;
  unit?: string | null;
  checkinPatch?: Record<string, unknown> | null;
  pendingSyncKeys?: string[];
  pendingCheckinKeys?: string[];
  pendingSyncAfterKeys?: string[];
  pendingSyncKeySummary?: string | null;
  pendingCheckinKeySummary?: string | null;
  pendingSyncAfterKeySummary?: string | null;
  syncRunId?: string | null;
  syncSequence?: number | null;
  preservedTrueCheckinKeys?: string[];
  preservedTrueCheckinKeySummary?: string | null;
  queryCheckinWeightLoggedBefore?: boolean | null;
  queryCheckinWeightLoggedIncoming?: boolean | null;
  queryCheckinWeightLoggedAfter?: boolean | null;
  queryCheckinRoutineAcknowledgedAtAfter?: string | null;
  weightResult?: {
    success: boolean;
    logId: string | null;
    loggedAt: string | null;
    unit: string | null;
  } | null;
  weightSuccess?: boolean | null;
  weightLogId?: string | null;
  weightLoggedAt?: string | null;
  persistedWeightUnit?: string | null;
  checkinResult?: {
    success: boolean;
    checkinDate: string | null;
    weightLogged: boolean | null;
    breakfastLogged: boolean | null;
    measurementsLogged: boolean | null;
    photoUploaded: boolean | null;
    videoUploaded: boolean | null;
    routineAcknowledgedAt: string | null;
  } | null;
  checkinSuccess?: boolean | null;
  checkinDate?: string | null;
  checkinWeightLogged?: boolean | null;
  checkinBreakfastLogged?: boolean | null;
  checkinMeasurementsLogged?: boolean | null;
  checkinPhotoUploaded?: boolean | null;
  checkinVideoUploaded?: boolean | null;
  checkinRoutineAcknowledgedAt?: string | null;
  durationMs?: number | null;
  error?: string | null;
  details?: Record<string, unknown>;
};

export type ReportDebugSliderTrailEntry = {
  atIso: string;
  routePath: string;
  source: string;
  event:
    | "slider-pointer-down"
    | "slider-change"
    | "slider-change-gap"
    | "slider-pointer-up"
    | "slider-pointer-cancel";
  sliderId: string;
  surface: string;
  label: string;
  value: number;
  previousValue: number | null;
  min: number;
  max: number;
  step: number;
  inputMode: "pointer" | "keyboard" | "programmatic" | "unknown";
  activeDurationMs: number | null;
  sincePreviousEventMs: number | null;
  changeCount: number;
  pointerType?: string | null;
  pointerMoveCount?: number;
  sincePointerMoveMs?: number | null;
  sincePointerDownMs?: number | null;
  lastPointerClientX?: number | null;
  lastPointerClientY?: number | null;
  renderCommitLatencyMs?: number | null;
  previousDebugWriteDurationMs?: number | null;
  details?: Record<string, unknown>;
};

export type ReportDebugProgramSelectorDragTrailEntry = {
  atIso: string;
  routePath: string;
  source: "program-selector-drag";
  event:
    | "program-selector-initial-scroll"
    | "program-selector-lock-state"
    | "program-selector-scroll-clamped"
    | "program-selector-reveal-requested"
    | "program-selector-reveal-animation-start"
    | "program-selector-reveal-animation-complete"
    | "program-selector-post-reveal-settle"
    | "program-selector-touch-start"
    | "program-selector-touch-move-sample"
    | "program-selector-touch-move-blocked"
    | "program-selector-touch-reveal-threshold"
    | "program-selector-touch-end"
    | "program-selector-gesture-end"
    | "program-selector-wheel-sample"
    | "program-selector-wheel-blocked"
    | "program-selector-wheel-reveal-threshold"
    | "program-selector-clamp-burst";
  surface: "program" | "analysis" | "unknown";
  selectionKey: string | null;
  gestureId: number | null;
  inputMode: "touch" | "wheel" | "programmatic" | "scroll" | "unknown";
  locked: boolean;
  revealProgress: number | null;
  pullDistance: number | null;
  lockedScrollTop: number | null;
  scrollTop: number | null;
  deltaY?: number | null;
  currentY?: number | null;
  previousY?: number | null;
  touchStartY?: number | null;
  touchMoveCount?: number | null;
  wheelEventCount?: number | null;
  cancelable?: boolean | null;
  defaultPrevented?: boolean | null;
  preventedCount?: number | null;
  cancelableMoveCount?: number | null;
  nonCancelableMoveCount?: number | null;
  attemptedScrollTop?: number | null;
  clampDeltaPx?: number | null;
  clampReason?: string | null;
  clampBurstId?: number | null;
  clampCount?: number | null;
  burstDurationMs?: number | null;
  maxClampDeltaPx?: number | null;
  avgClampIntervalMs?: number | null;
  startedAfterRevealMs?: number | null;
  settleDurationMs?: number | null;
  settleFrameCount?: number | null;
  finalScrollTop?: number | null;
  maxScrollTop?: number | null;
  maxScrollPastLockedPx?: number | null;
  reboundPastLocked?: boolean | null;
  sincePreviousEventMs?: number | null;
  handlerDurationMs?: number | null;
  frameCount?: number | null;
  maxFrameGapMs?: number | null;
  longFrameCount?: number | null;
  approxFps?: number | null;
  previousDebugWriteDurationMs?: number | null;
  details?: Record<string, unknown>;
};

export type ReportDebugGoalsEditTrailEntry = {
  atIso: string;
  routePath: string;
  source: "measurements-goals" | "weight-goal-edit-form";
  event:
    | "goals-surface-mounted"
    | "goals-surface-unmounted"
    | "edit-mode-changed"
    | "edit-form-mounted"
    | "edit-form-unmounted"
    | "edit-form-load-start"
    | "edit-form-load-finish"
    | "maintenance-weeks-focus"
    | "maintenance-weeks-change"
    | "maintenance-weeks-blur";
  goalId: string | null;
  activeGoalId?: string | null;
  isEditing?: boolean | null;
  field?: "total" | "remaining" | null;
  draftValue?: string | null;
  parsedValue?: number | null;
  currentValue?: number | null;
  maintenanceWeeks?: number | null;
  maintenanceWeeksRemaining?: number | null;
  reason?: string | null;
  requestId?: number | null;
  success?: boolean | null;
  error?: string | null;
};

export type ReportDebugGalleryDataState = {
  snapshotAtIso: string;
  routePath: string | null;
  requestedTab?: string | null;
  activeTab?: string | null;
  uploadsCount: number;
  comparisonsCount: number;
  uploadPaging?: {
    firstPageLimit: number;
    firstPageCount: number;
    renderedUploadCount: number;
    loadedUploadCount: number;
    nextCursorPresent: boolean;
    loadMoreCount: number;
    latestLoadMoreDurationMs: number | null;
    latestLoadMoreAddedCount: number | null;
    totalKnownCount: number | null;
    carouselPrefetchCount?: number;
    carouselHighlightedCount?: number;
    carouselHiddenPrefetchCount?: number;
  };
  comparisonPaging?: {
    firstPageLimit: number;
    firstPageCount: number;
    renderedComparisonCount: number;
    loadedComparisonCount: number;
    nextCursorPresent: boolean;
    loadMoreCount: number;
    latestLoadMoreDurationMs: number | null;
    latestLoadMoreAddedCount: number | null;
    totalKnownCount: number | null;
    comparisonMediaPrefetchCount: number;
  };
  fetch?: {
    totalDurationMs: number;
    profileDurationMs: number;
    createClientDurationMs: number;
    initialParallelDurationMs: number;
    uploadRowsDurationMs: number;
    comparisonsDurationMs: number;
    comparisonItemsDurationMs: number;
    comparisonMediaRowsDurationMs: number;
    commentsDurationMs: number;
    limitsDurationMs: number;
    usageDurationMs: number;
    annotationSummaryDurationMs: number;
    checkInLinksDurationMs: number;
    counts: {
      uploads: number;
      comparisons: number;
      comparisonItems: number;
      comparisonComments: number;
      comparisonMediaRows: number;
      annotationMediaIds: number;
      checkInIds: number;
      checkInRows: number;
    };
  };
  videoUploadDuplicateSummary?: {
    videoUploadTotal: number;
    videoUploadWithAssetId: number;
    videoUploadWithSourceCheckIn: number;
    duplicateVideoAssetCount: number;
    duplicateVideoUploadCount: number;
    duplicateVideoAssetSamples: string[];
  };
  capturedOnSchemaFallback: {
    uploads: boolean;
    comparisonMedia: boolean;
  };
  capturedDateCounts: {
    uploadPhotoTotal: number;
    uploadPhotoWithCapturedOn: number;
    uploadPhotoWithRenderableLabel: number;
    comparisonPhotoItemTotal: number;
    comparisonPhotoItemWithCapturedOn: number;
    comparisonPhotoItemWithRenderableLabel: number;
  };
  capturedDateRecentUploadPhotoSamples: string[];
  capturedDateComparisonMissingSamples: string[];
};

type GalleryCapturedDateSource = "native" | "exif" | "none";

export type ReportDebugGalleryCapturedDateTrailEntry = {
  atIso: string;
  routePath: string | null;
  source: string;
  event: "photo-captured-date-resolved";
  resolutionSource: GalleryCapturedDateSource;
  capturedOnAvailable: boolean;
  nativeHintAvailable: boolean;
  fileType: string | null;
  fileSizeBucket: string;
};

export type ReportDebugGalleryUploadProcessingState = {
  snapshotAtIso: string;
  routePath: string | null;
  event: string;
  reason: string | null;
  fileName: string | null;
  uploadId: string | null;
  assetId: string | null;
  uploadStatus: string | null;
  assetStatus: string | null;
  ready: boolean | null;
  finalized: boolean | null;
  finalizerStatus: string | null;
  finalizerCheckInId: string | null;
  finalizerCreatedMedia: boolean | null;
  finalizerReason: string | null;
  resolvedMediaId?: string | null;
  baselineUploadCount?: number | null;
  currentUploadCount?: number | null;
  error?: string | null;
};

export type ReportDebugVideoUploadState = {
  snapshotAtIso: string;
  routePath: string | null;
  status: string;
  fileName: string | null;
  progress: number;
  uploadId: string | null;
  attemptId: string | null;
  transferMode: string | null;
  errorMessage: string | null;
  updatedAtIso: string | null;
};

export type ReportDebugMediaUploadPerfTrailEntry = {
  atIso: string;
  routePath: string | null;
  source: string;
  event:
    | "photo-picker-complete"
    | "photo-upload-batch-start"
    | "photo-upload-auth-complete"
    | "photo-upload-file-start"
    | "photo-upload-captured-date-complete"
    | "photo-upload-storage-complete"
    | "photo-upload-save-batch-start"
    | "photo-upload-save-batch-complete"
    | "photo-upload-save-complete"
    | "photo-upload-file-complete"
    | "photo-upload-file-validation-error"
    | "photo-upload-success-indicator-shown"
    | "photo-upload-success-indicator-cleared"
    | "photo-upload-batch-complete";
  flowId: string;
  mediaType: "photo" | "video" | "unknown";
  fileIndex: number | null;
  fileCount: number | null;
  fileSizeBucket: string | null;
  fileSizeBytes: number | null;
  durationMs: number | null;
  totalDurationMs: number | null;
  stage: string | null;
  success: boolean | null;
  error: string | null;
  details?: Record<string, unknown>;
};

export type ReportDebugGalleryPreviewPerfTrailEntry = {
  atIso: string;
  routePath: string | null;
  source: "gallery-preview";
  event:
    | "preview-index-change-requested"
    | "preview-media-committed"
    | "preview-photo-load"
    | "preview-photo-error"
    | "preview-backdrop-load"
    | "preview-backdrop-error"
    | "preview-tone-load"
    | "preview-tone-sampled"
    | "preview-tone-error";
  transitionId: string;
  reason: "open" | "next" | "previous" | "swipe" | "wheel" | "unknown";
  mediaId: string | null;
  mediaType: "photo" | "video" | "unknown";
  fromIndex: number | null;
  previewIndex: number | null;
  totalItems: number | null;
  durationMs: number | null;
  stage: string | null;
  success: boolean | null;
  details?: Record<string, unknown>;
};

export type ReportDebugDashboardOverviewChartRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
} | null;

export type ReportDebugDashboardOverviewChartState = {
  snapshotAtIso: string;
  routePath: string | null;
  source: "client-dashboard-overview";
  reason: string;
  platform: string | null;
  viewport: {
    innerWidth: number;
    innerHeight: number;
    visualViewportWidth: number | null;
    visualViewportHeight: number | null;
    visualViewportScale: number | null;
    devicePixelRatio: number | null;
    orientationType: string | null;
  };
  rects: {
    card: ReportDebugDashboardOverviewChartRect;
    chart: ReportDebugDashboardOverviewChartRect;
    main: ReportDebugDashboardOverviewChartRect;
  };
  chart: {
    measuredWidth: number;
    measuredHeight: number;
    viewBoxWidth: number;
    viewBoxHeight: number;
    chartLeft: number;
    chartRight: number;
    chartTop: number;
    chartBottom: number;
    plotWidth: number;
    plotHeight: number;
    plotWidthRatio: number;
    yLabelX: number;
    maxValue: number;
    pointCount: number;
    visiblePointLabelCount: number;
    hiddenPointLabelCount: number;
  };
  trend: Array<{
    key: string;
    label: string;
    calories: number;
  }>;
  labels: {
    pointLabels: Array<Record<string, unknown>>;
    yTickLabels: Array<Record<string, unknown>>;
    xTickLabels: Array<Record<string, unknown>>;
  };
  css: {
    card: Record<string, string | null>;
    chart: Record<string, string | null>;
    main: Record<string, string | null>;
  };
};

export type ReportDebugDashboardOverviewChartStateInput = Omit<
  ReportDebugDashboardOverviewChartState,
  "routePath" | "snapshotAtIso"
> & {
  routePath?: string | null;
};

export type ReportDebugDashboardDailyCaloriesRect = ReportDebugDashboardOverviewChartRect;

export type ReportDebugDashboardDailyCaloriesMacroRowSnapshot = {
  key: string;
  label: string;
  value: number;
  target: number | null;
  percent: number;
  rects: {
    row: ReportDebugDashboardDailyCaloriesRect;
    labelRow: ReportDebugDashboardDailyCaloriesRect;
    label: ReportDebugDashboardDailyCaloriesRect;
    values: ReportDebugDashboardDailyCaloriesRect;
    percent: ReportDebugDashboardDailyCaloriesRect;
    track: ReportDebugDashboardDailyCaloriesRect;
    fill: ReportDebugDashboardDailyCaloriesRect;
  };
  css: {
    row: Record<string, string | null>;
    labelRow: Record<string, string | null>;
    label: Record<string, string | null>;
    values: Record<string, string | null>;
    percent: Record<string, string | null>;
    track: Record<string, string | null>;
    fill: Record<string, string | null>;
  };
};

export type ReportDebugDashboardDailyCaloriesState = {
  snapshotAtIso: string;
  routePath: string | null;
  source: "client-dashboard-daily-calories";
  reason: string;
  platform: string | null;
  viewport: {
    innerWidth: number;
    innerHeight: number;
    visualViewportWidth: number | null;
    visualViewportHeight: number | null;
    visualViewportScale: number | null;
    devicePixelRatio: number | null;
    orientationType: string | null;
  };
  shell: {
    clientShellHeight: string | null;
    clientMobileTabsHeight: string | null;
    clientBottomSafeArea: string | null;
    clientKeyboardInset: string | null;
    clientHomeRoute: string | null;
    routeTransitionTransform: string | null;
  };
  rects: {
    main: ReportDebugDashboardDailyCaloriesRect;
    card: ReportDebugDashboardDailyCaloriesRect;
    header: ReportDebugDashboardDailyCaloriesRect;
    body: ReportDebugDashboardDailyCaloriesRect;
    dailyCalories: ReportDebugDashboardDailyCaloriesRect;
    calorieRing: ReportDebugDashboardDailyCaloriesRect;
    calorieGoal: ReportDebugDashboardDailyCaloriesRect;
    macroList: ReportDebugDashboardDailyCaloriesRect;
  };
  css: {
    main: Record<string, string | null>;
    card: Record<string, string | null>;
    header: Record<string, string | null>;
    body: Record<string, string | null>;
    dailyCalories: Record<string, string | null>;
    calorieRing: Record<string, string | null>;
    calorieGoal: Record<string, string | null>;
    macroList: Record<string, string | null>;
  };
  layout: {
    contentBottom: number | null;
    cardBottom: number | null;
    mainBottom: number | null;
    viewportBottom: number;
    bodyOverflowBottomPx: number | null;
    cardOverflowBottomPx: number | null;
    mainOverflowBottomPx: number | null;
    viewportOverflowBottomPx: number | null;
    macroListOverflowBottomPx: number | null;
    macroRowsTotalHeight: number | null;
    macroRowGapPx: Array<number | null>;
  };
  calories: {
    value: number;
    target: number;
    percent: number;
  };
  macros: ReportDebugDashboardDailyCaloriesMacroRowSnapshot[];
};

export type ReportDebugDashboardDailyCaloriesStateInput = Omit<
  ReportDebugDashboardDailyCaloriesState,
  "routePath" | "snapshotAtIso"
> & {
  routePath?: string | null;
};

function safeParseJson(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readPersistedSessionJson<T>(key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writePersistedSessionJson(key: string, snapshot: unknown) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Debug-only persistence must never block the app.
  }
}

function readPersistedLocalJson<T>(key: string): T | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writePersistedLocalJson(key: string, snapshot: unknown) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(snapshot));
  } catch (error) {
    console.warn("[ReportDebug] localStorage.setItem failed for key", key, error);
  }
}

function truncateString(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 3)}...`;
}

export function sanitizeClientDebugValue(value: unknown, depth = 0, maxDepth = SNAPSHOT_DETAILS_DEPTH_MAX): unknown {
  if (value == null) return value;
  if (typeof value === "string") return truncateString(value, SNAPSHOT_DETAILS_STRING_MAX);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= maxDepth) return "[truncated]";
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeClientDebugValue(item, depth + 1, maxDepth));
  }
  if (typeof value === "object") {
    const result: Record<string, unknown> = {};
    const entries = Object.entries(value as Record<string, unknown>).slice(0, 100);
    for (const [key, entryValue] of entries) {
      result[key] = sanitizeClientDebugValue(entryValue, depth + 1, maxDepth);
    }
    return result;
  }
  return String(value);
}

export function sanitizeClientDebugAction(action: ClientDebugAction): ClientDebugAction {
  return {
    atIso: action.atIso,
    source: action.source,
    event: action.event,
    details: action.details
      ? (sanitizeClientDebugValue(action.details) as Record<string, unknown>)
      : undefined,
  };
}

function clampArray<T>(input: unknown, maxItems: number): T[] | undefined {
  if (!Array.isArray(input)) return undefined;
  return input.slice(Math.max(0, input.length - maxItems)) as T[];
}

function toMs(input: string | undefined): number | null {
  if (!input) return null;
  const parsed = Date.parse(input);
  return Number.isFinite(parsed) ? parsed : null;
}

function getFileSizeBucket(sizeBytes: number | null | undefined): string {
  if (typeof sizeBytes !== "number" || !Number.isFinite(sizeBytes) || sizeBytes < 0) return "unknown";
  const mb = sizeBytes / (1024 * 1024);
  if (mb < 1) return "<1MB";
  if (mb < 5) return "1-5MB";
  if (mb < 10) return "5-10MB";
  if (mb < 25) return "10-25MB";
  return "25MB+";
}

function readPersistedSessionArray<T>(key: string): T[] {
  const parsed = readPersistedSessionJson<T[]>(key);
  return Array.isArray(parsed) ? parsed : [];
}

function summarizeWorkoutStore(data: StoredWorkoutData | null): Record<string, unknown> | null {
  if (!data) return null;

  const currentExercise = data.exercises?.[data.activeExerciseIndex] ?? null;
  const currentExerciseId = currentExercise?.id ?? null;

  return {
    sessionId: data.sessionId,
    programDayId: data.programDayId,
    programDayName: data.programDayName,
    activeExerciseIndex: data.activeExerciseIndex,
    exerciseCount: data.exercises?.length ?? 0,
    currentExerciseId,
    currentExerciseName: currentExercise?.exercises?.name ?? null,
    currentSetCount: currentExerciseId ? data.sets[currentExerciseId]?.length ?? 0 : null,
    currentSavedSetNumbers: currentExerciseId ? data.savedSetNumbers[currentExerciseId] ?? [] : [],
    sessionNotes: data.sessionNotes,
    lastUpdated: data.lastUpdated,
    hasModifications: (data.modifications?.length ?? 0) > 0,
  };
}

function summarizeWorkoutDebugState(state: ReportDebugWorkoutState | null): Record<string, unknown> | null {
  if (!state) return null;
  return sanitizeClientDebugValue(state) as Record<string, unknown>;
}

export function persistReportDebugWorkoutState(state: ReportDebugWorkoutState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_WORKOUT_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_WORKOUT_STATE_KEY, state);
}

export function readReportDebugWorkoutState(): ReportDebugWorkoutState | null {
  return readPersistedSessionJson<ReportDebugWorkoutState>(REPORT_DEBUG_WORKOUT_STATE_KEY);
}

export function persistReportDebugOnboardingTipState(state: ReportDebugOnboardingTipState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.localStorage.removeItem(REPORT_DEBUG_ONBOARDING_TIP_STATE_KEY);
    return;
  }
  writePersistedLocalJson(REPORT_DEBUG_ONBOARDING_TIP_STATE_KEY, state);
}

export function readReportDebugOnboardingTipState(): ReportDebugOnboardingTipState | null {
  return readPersistedLocalJson<ReportDebugOnboardingTipState>(REPORT_DEBUG_ONBOARDING_TIP_STATE_KEY);
}

function readReportDebugPullToRefreshStateRaw(): ReportDebugPullToRefreshState | null {
  return readPersistedSessionJson<ReportDebugPullToRefreshState>(REPORT_DEBUG_PULL_TO_REFRESH_STATE_KEY);
}

export function persistReportDebugPullToRefreshState(state: ReportDebugPullToRefreshState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_PULL_TO_REFRESH_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_PULL_TO_REFRESH_STATE_KEY, state);
}

export function readReportDebugPullToRefreshState(): ReportDebugPullToRefreshState | null {
  return readReportDebugPullToRefreshStateRaw();
}

export function persistReportDebugGalleryDataState(state: ReportDebugGalleryDataState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_GALLERY_DATA_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_GALLERY_DATA_STATE_KEY, sanitizeClientDebugValue(state));
}

export function readReportDebugGalleryDataState(): ReportDebugGalleryDataState | null {
  return readPersistedSessionJson<ReportDebugGalleryDataState>(REPORT_DEBUG_GALLERY_DATA_STATE_KEY);
}

export function appendReportDebugGalleryCapturedDateTrailEvent(input: {
  source: string;
  resolutionSource: GalleryCapturedDateSource;
  capturedOnAvailable: boolean;
  nativeHintAvailable: boolean;
  fileType: string | null;
  fileSizeBytes?: number | null;
  routePath?: string | null;
}): void {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugGalleryCapturedDateTrailEntry = {
    atIso: new Date().toISOString(),
    routePath: input.routePath ?? `${window.location.pathname}${window.location.search}`,
    source: input.source,
    event: "photo-captured-date-resolved",
    resolutionSource: input.resolutionSource,
    capturedOnAvailable: input.capturedOnAvailable,
    nativeHintAvailable: input.nativeHintAvailable,
    fileType: input.fileType,
    fileSizeBucket: getFileSizeBucket(input.fileSizeBytes),
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugGalleryCapturedDateTrailEntry>(REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_KEY),
    nextItem,
  ].slice(-REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_KEY, sanitizeClientDebugValue(next));
}

export function readReportDebugGalleryCapturedDateTrail(
  limit = REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_MAX
): ReportDebugGalleryCapturedDateTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugGalleryCapturedDateTrailEntry>(
    REPORT_DEBUG_GALLERY_CAPTURED_DATE_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function persistReportDebugGalleryUploadProcessingState(
  state: ReportDebugGalleryUploadProcessingState | null
): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_GALLERY_UPLOAD_PROCESSING_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_GALLERY_UPLOAD_PROCESSING_STATE_KEY, sanitizeClientDebugValue(state));
}

export function readReportDebugGalleryUploadProcessingState(): ReportDebugGalleryUploadProcessingState | null {
  return readPersistedSessionJson<ReportDebugGalleryUploadProcessingState>(
    REPORT_DEBUG_GALLERY_UPLOAD_PROCESSING_STATE_KEY
  );
}

export function persistReportDebugVideoUploadState(state: ReportDebugVideoUploadState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_VIDEO_UPLOAD_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_VIDEO_UPLOAD_STATE_KEY, sanitizeClientDebugValue(state));
}

export function readReportDebugVideoUploadState(): ReportDebugVideoUploadState | null {
  return readPersistedSessionJson<ReportDebugVideoUploadState>(REPORT_DEBUG_VIDEO_UPLOAD_STATE_KEY);
}

export function appendReportDebugMediaUploadPerfTrailEvent(input: {
  source: string;
  event: ReportDebugMediaUploadPerfTrailEntry["event"];
  flowId: string;
  mediaType?: "photo" | "video" | "unknown";
  fileIndex?: number | null;
  fileCount?: number | null;
  fileSizeBytes?: number | null;
  durationMs?: number | null;
  totalDurationMs?: number | null;
  stage?: string | null;
  success?: boolean | null;
  error?: string | null;
  details?: Record<string, unknown>;
  routePath?: string | null;
}): void {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugMediaUploadPerfTrailEntry = {
    atIso: new Date().toISOString(),
    routePath: input.routePath ?? `${window.location.pathname}${window.location.search}`,
    source: input.source,
    event: input.event,
    flowId: input.flowId,
    mediaType: input.mediaType ?? "unknown",
    fileIndex: input.fileIndex ?? null,
    fileCount: input.fileCount ?? null,
    fileSizeBucket: input.fileSizeBytes == null ? null : getFileSizeBucket(input.fileSizeBytes),
    fileSizeBytes: input.fileSizeBytes ?? null,
    durationMs: input.durationMs ?? null,
    totalDurationMs: input.totalDurationMs ?? null,
    stage: input.stage ?? null,
    success: input.success ?? null,
    error: input.error ?? null,
    details: input.details,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugMediaUploadPerfTrailEntry>(REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_KEY),
    nextItem,
  ].slice(-REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_MAX);
  writePersistedSessionJson(
    REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_KEY,
    next.map((item) => sanitizeClientDebugValue(item) as ReportDebugMediaUploadPerfTrailEntry)
  );
}

export function readReportDebugMediaUploadPerfTrail(
  limit = REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_MAX
): ReportDebugMediaUploadPerfTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugMediaUploadPerfTrailEntry>(
    REPORT_DEBUG_MEDIA_UPLOAD_PERF_TRAIL_KEY
  ).slice(-limit);
}

export function appendReportDebugGalleryPreviewPerfTrailEvent(input: {
  event: ReportDebugGalleryPreviewPerfTrailEntry["event"];
  transitionId: string;
  reason?: ReportDebugGalleryPreviewPerfTrailEntry["reason"];
  mediaId?: string | null;
  mediaType?: "photo" | "video" | "unknown";
  fromIndex?: number | null;
  previewIndex?: number | null;
  totalItems?: number | null;
  durationMs?: number | null;
  stage?: string | null;
  success?: boolean | null;
  details?: Record<string, unknown>;
  routePath?: string | null;
}): void {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugGalleryPreviewPerfTrailEntry = {
    atIso: new Date().toISOString(),
    routePath: input.routePath ?? `${window.location.pathname}${window.location.search}`,
    source: "gallery-preview",
    event: input.event,
    transitionId: input.transitionId,
    reason: input.reason ?? "unknown",
    mediaId: input.mediaId ?? null,
    mediaType: input.mediaType ?? "unknown",
    fromIndex: input.fromIndex ?? null,
    previewIndex: input.previewIndex ?? null,
    totalItems: input.totalItems ?? null,
    durationMs: input.durationMs ?? null,
    stage: input.stage ?? null,
    success: input.success ?? null,
    details: input.details,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugGalleryPreviewPerfTrailEntry>(REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_KEY),
    nextItem,
  ].slice(-REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_MAX);
  writePersistedSessionJson(
    REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_KEY,
    next.map((item) => sanitizeClientDebugValue(item) as ReportDebugGalleryPreviewPerfTrailEntry)
  );
}

export function readReportDebugGalleryPreviewPerfTrail(
  limit = REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_MAX
): ReportDebugGalleryPreviewPerfTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugGalleryPreviewPerfTrailEntry>(
    REPORT_DEBUG_GALLERY_PREVIEW_PERF_TRAIL_KEY
  ).slice(-limit);
}

export function persistReportDebugDashboardOverviewChartState(
  input: ReportDebugDashboardOverviewChartStateInput | null
): void {
  if (typeof window === "undefined") return;
  if (!input) {
    window.sessionStorage.removeItem(REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_STATE_KEY);
    window.sessionStorage.removeItem(REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_KEY);
    return;
  }

  const nextItem: ReportDebugDashboardOverviewChartState = {
    ...input,
    snapshotAtIso: new Date().toISOString(),
    routePath: input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
  };
  const sanitized = sanitizeClientDebugValue(nextItem, 0, 6) as ReportDebugDashboardOverviewChartState;
  writePersistedSessionJson(REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_STATE_KEY, sanitized);
  const nextTrail = [
    ...readPersistedSessionArray<ReportDebugDashboardOverviewChartState>(
      REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_KEY
    ),
    sanitized,
  ].slice(-REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_KEY, nextTrail);
}

export function readReportDebugDashboardOverviewChartState(): ReportDebugDashboardOverviewChartState | null {
  return readPersistedSessionJson<ReportDebugDashboardOverviewChartState>(
    REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_STATE_KEY
  );
}

export function readReportDebugDashboardOverviewChartTrail(
  limit = REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_MAX
): ReportDebugDashboardOverviewChartState[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugDashboardOverviewChartState>(
    REPORT_DEBUG_DASHBOARD_OVERVIEW_CHART_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function persistReportDebugDashboardDailyCaloriesState(
  input: ReportDebugDashboardDailyCaloriesStateInput | null
): void {
  if (typeof window === "undefined") return;
  if (!input) {
    window.sessionStorage.removeItem(REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_STATE_KEY);
    window.sessionStorage.removeItem(REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_KEY);
    return;
  }

  const nextItem: ReportDebugDashboardDailyCaloriesState = {
    ...input,
    snapshotAtIso: new Date().toISOString(),
    routePath: input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
  };
  const sanitized = sanitizeClientDebugValue(nextItem, 0, 6) as ReportDebugDashboardDailyCaloriesState;
  writePersistedSessionJson(REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_STATE_KEY, sanitized);
  const nextTrail = [
    ...readPersistedSessionArray<ReportDebugDashboardDailyCaloriesState>(
      REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_KEY
    ),
    sanitized,
  ].slice(-REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_KEY, nextTrail);
}

export function readReportDebugDashboardDailyCaloriesState(): ReportDebugDashboardDailyCaloriesState | null {
  return readPersistedSessionJson<ReportDebugDashboardDailyCaloriesState>(
    REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_STATE_KEY
  );
}

export function readReportDebugDashboardDailyCaloriesTrail(
  limit = REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_MAX
): ReportDebugDashboardDailyCaloriesState[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugDashboardDailyCaloriesState>(
    REPORT_DEBUG_DASHBOARD_DAILY_CALORIES_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function appendReportDebugPullToRefreshTrailEvent(input: {
  source: string;
  event: string;
  routePath?: string | null;
  details?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ClientDebugAction = {
    atIso: new Date().toISOString(),
    source: input.source,
    event: input.event,
    details: sanitizeClientDebugValue({
      routePath: input.routePath ?? null,
      ...(input.details || {}),
    }) as Record<string, unknown>,
  };
  const next = [...readPersistedSessionArray<ClientDebugAction>(REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_KEY), nextItem].slice(
    -REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_MAX
  );
  writePersistedSessionJson(REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_KEY, next);
}

export function readReportDebugPullToRefreshTrail(limit = REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_MAX): ClientDebugAction[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ClientDebugAction>(REPORT_DEBUG_PULL_TO_REFRESH_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function persistReportDebugHistoryQueryState(state: ReportDebugHistoryQueryState | null): void {
  if (typeof window === "undefined") return;
  if (!state) {
    window.sessionStorage.removeItem(REPORT_DEBUG_HISTORY_QUERY_STATE_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_HISTORY_QUERY_STATE_KEY, sanitizeClientDebugValue(state));
}

export function readReportDebugHistoryQueryState(): ReportDebugHistoryQueryState | null {
  return readPersistedSessionJson<ReportDebugHistoryQueryState>(REPORT_DEBUG_HISTORY_QUERY_STATE_KEY);
}

export function appendReportDebugNutritionCustomFoodTrailEvent(input: {
  routePath?: string | null;
  event: ReportDebugNutritionCustomFoodTrailEntry["event"];
  entryPoint?: ReportDebugNutritionCustomFoodTrailEntry["entryPoint"];
  isEditing?: boolean;
  hasBarcode?: boolean;
  sharePublicly?: boolean | null;
  foodId?: string | null;
  foodName?: string | null;
  closeReason?: ReportDebugNutritionCustomFoodTrailEntry["closeReason"];
  error?: string | null;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugNutritionCustomFoodTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    event: input.event,
    entryPoint: input.entryPoint ?? "unknown",
    isEditing: input.isEditing ?? false,
    hasBarcode: input.hasBarcode ?? false,
    sharePublicly: input.sharePublicly ?? null,
    foodId: input.foodId ?? null,
    foodName: input.foodName ?? null,
    closeReason: input.closeReason ?? null,
    error: input.error ?? null,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugNutritionCustomFoodTrailEntry>(
      REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_KEY
    ),
    nextItem,
  ].slice(-REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_KEY, next);
}

export function readReportDebugNutritionCustomFoodTrail(
  limit = REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_MAX
): ReportDebugNutritionCustomFoodTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugNutritionCustomFoodTrailEntry>(
    REPORT_DEBUG_NUTRITION_CUSTOM_FOOD_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function appendReportDebugNutritionSearchTrailEvent(input: Omit<ReportDebugNutritionSearchTrailEntry, "atIso" | "routePath"> & {
  routePath?: string | null;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugNutritionSearchTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    event: input.event,
    query: input.query,
    normalizedQuery: input.normalizedQuery,
    enabled: input.enabled,
    durationMs: input.durationMs,
    resultCount: input.resultCount,
    cacheHit: input.cacheHit,
    error: input.error,
    topResults: input.topResults,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugNutritionSearchTrailEntry>(
      REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_KEY
    ),
    sanitizeClientDebugValue(nextItem) as ReportDebugNutritionSearchTrailEntry,
  ].slice(-REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_KEY, next);
}

export function readReportDebugNutritionSearchTrail(
  limit = REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_MAX
): ReportDebugNutritionSearchTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugNutritionSearchTrailEntry>(
    REPORT_DEBUG_NUTRITION_SEARCH_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function appendReportDebugNutritionLogTrailEvent(input: Omit<ReportDebugNutritionLogTrailEntry, "atIso" | "routePath"> & {
  routePath?: string | null;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugNutritionLogTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    event: input.event,
    flowId: input.flowId,
    sourceSurface: input.sourceSurface,
    targetDate: input.targetDate,
    mealType: input.mealType,
    activeTab: input.activeTab,
    tempIds: input.tempIds,
    persistedIds: input.persistedIds,
    logsBefore: input.logsBefore,
    logsAfter: input.logsAfter,
    dayLogsBefore: input.dayLogsBefore,
    dayLogsAfter: input.dayLogsAfter,
    cacheWriteMs: input.cacheWriteMs,
    intentToOptimisticMs: input.intentToOptimisticMs,
    optimisticToVisibleMs: input.optimisticToVisibleMs,
    persistMs: input.persistMs,
    refetchMs: input.refetchMs,
    error: input.error,
    serverTiming: input.serverTiming ? sanitizeClientDebugValue(input.serverTiming) : undefined,
    serverTimingPresent: input.serverTimingPresent,
    serverTimingMissingReason: input.serverTimingMissingReason,
    actionResultKeys: input.actionResultKeys,
    debugResultKeys: input.debugResultKeys,
    serverTimingKeys: input.serverTimingKeys,
    pendingSyncCount: input.pendingSyncCount,
    pendingSyncTypes: input.pendingSyncTypes,
    pendingAddCalorieLogCount: input.pendingAddCalorieLogCount,
    pendingAddCalorieLogTempIds: input.pendingAddCalorieLogTempIds,
    pendingAddCalorieLogTargetDates: input.pendingAddCalorieLogTargetDates,
    pendingAddCalorieLogMealTypes: input.pendingAddCalorieLogMealTypes,
    pendingAddCalorieLogSources: input.pendingAddCalorieLogSources,
    visiblePendingAddCalorieLogTempIds: input.visiblePendingAddCalorieLogTempIds,
    previousVisiblePendingAddCalorieLogTempIds: input.previousVisiblePendingAddCalorieLogTempIds,
    droppedPendingAddCalorieLogTempIds: input.droppedPendingAddCalorieLogTempIds,
    details: input.details ? (sanitizeClientDebugValue(input.details) as Record<string, unknown>) : undefined,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugNutritionLogTrailEntry>(REPORT_DEBUG_NUTRITION_LOG_TRAIL_KEY),
    sanitizeClientDebugValue(nextItem) as ReportDebugNutritionLogTrailEntry,
  ].slice(-REPORT_DEBUG_NUTRITION_LOG_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_NUTRITION_LOG_TRAIL_KEY, next);
}

export function readReportDebugNutritionLogTrail(
  limit = REPORT_DEBUG_NUTRITION_LOG_TRAIL_MAX
): ReportDebugNutritionLogTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugNutritionLogTrailEntry>(REPORT_DEBUG_NUTRITION_LOG_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function appendReportDebugDailyRoutineTrailEvent(
  input: Omit<ReportDebugDailyRoutineTrailEntry, "atIso" | "routePath"> & {
    routePath?: string | null;
  }
) {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugDailyRoutineTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    event: input.event,
    flowId: input.flowId,
    step: input.step,
    tempId: input.tempId,
    unit: input.unit,
    checkinPatch: input.checkinPatch
      ? (sanitizeClientDebugValue(input.checkinPatch) as Record<string, unknown>)
      : undefined,
    pendingSyncKeys: input.pendingSyncKeys,
    pendingCheckinKeys: input.pendingCheckinKeys,
    pendingSyncAfterKeys: input.pendingSyncAfterKeys,
    pendingSyncKeySummary: input.pendingSyncKeySummary ?? input.pendingSyncKeys?.join(",") ?? null,
    pendingCheckinKeySummary: input.pendingCheckinKeySummary ?? input.pendingCheckinKeys?.join(",") ?? null,
    pendingSyncAfterKeySummary:
      input.pendingSyncAfterKeySummary ?? input.pendingSyncAfterKeys?.join(",") ?? null,
    syncRunId: input.syncRunId ?? null,
    syncSequence: input.syncSequence ?? null,
    preservedTrueCheckinKeys: input.preservedTrueCheckinKeys,
    preservedTrueCheckinKeySummary:
      input.preservedTrueCheckinKeySummary ?? input.preservedTrueCheckinKeys?.join(",") ?? null,
    queryCheckinWeightLoggedBefore: input.queryCheckinWeightLoggedBefore ?? null,
    queryCheckinWeightLoggedIncoming: input.queryCheckinWeightLoggedIncoming ?? null,
    queryCheckinWeightLoggedAfter: input.queryCheckinWeightLoggedAfter ?? null,
    queryCheckinRoutineAcknowledgedAtAfter: input.queryCheckinRoutineAcknowledgedAtAfter ?? null,
    weightResult: input.weightResult,
    weightSuccess: input.weightSuccess ?? input.weightResult?.success ?? null,
    weightLogId: input.weightLogId ?? input.weightResult?.logId ?? null,
    weightLoggedAt: input.weightLoggedAt ?? input.weightResult?.loggedAt ?? null,
    persistedWeightUnit: input.persistedWeightUnit ?? input.weightResult?.unit ?? null,
    checkinResult: input.checkinResult,
    checkinSuccess: input.checkinSuccess ?? input.checkinResult?.success ?? null,
    checkinDate: input.checkinDate ?? input.checkinResult?.checkinDate ?? null,
    checkinWeightLogged: input.checkinWeightLogged ?? input.checkinResult?.weightLogged ?? null,
    checkinBreakfastLogged: input.checkinBreakfastLogged ?? input.checkinResult?.breakfastLogged ?? null,
    checkinMeasurementsLogged: input.checkinMeasurementsLogged ?? input.checkinResult?.measurementsLogged ?? null,
    checkinPhotoUploaded: input.checkinPhotoUploaded ?? input.checkinResult?.photoUploaded ?? null,
    checkinVideoUploaded: input.checkinVideoUploaded ?? input.checkinResult?.videoUploaded ?? null,
    checkinRoutineAcknowledgedAt:
      input.checkinRoutineAcknowledgedAt ?? input.checkinResult?.routineAcknowledgedAt ?? null,
    durationMs: input.durationMs,
    error: input.error,
    details: input.details ? (sanitizeClientDebugValue(input.details) as Record<string, unknown>) : undefined,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugDailyRoutineTrailEntry>(REPORT_DEBUG_DAILY_ROUTINE_TRAIL_KEY),
    sanitizeClientDebugValue(nextItem) as ReportDebugDailyRoutineTrailEntry,
  ].slice(-REPORT_DEBUG_DAILY_ROUTINE_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_DAILY_ROUTINE_TRAIL_KEY, next);
}

export function readReportDebugDailyRoutineTrail(
  limit = REPORT_DEBUG_DAILY_ROUTINE_TRAIL_MAX
): ReportDebugDailyRoutineTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugDailyRoutineTrailEntry>(REPORT_DEBUG_DAILY_ROUTINE_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function appendReportDebugSliderTrailEvent(input: Omit<ReportDebugSliderTrailEntry, "atIso" | "routePath"> & {
  routePath?: string | null;
}): number | null {
  if (typeof window === "undefined") return null;
  const startedAtMs = typeof performance !== "undefined" ? performance.now() : Date.now();
  const nextItem: ReportDebugSliderTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    source: input.source,
    event: input.event,
    sliderId: input.sliderId,
    surface: input.surface,
    label: input.label,
    value: input.value,
    previousValue: input.previousValue,
    min: input.min,
    max: input.max,
    step: input.step,
    inputMode: input.inputMode,
    activeDurationMs: input.activeDurationMs,
    sincePreviousEventMs: input.sincePreviousEventMs,
    changeCount: input.changeCount,
    pointerType: input.pointerType,
    pointerMoveCount: input.pointerMoveCount,
    sincePointerMoveMs: input.sincePointerMoveMs,
    sincePointerDownMs: input.sincePointerDownMs,
    lastPointerClientX: input.lastPointerClientX,
    lastPointerClientY: input.lastPointerClientY,
    renderCommitLatencyMs: input.renderCommitLatencyMs,
    previousDebugWriteDurationMs: input.previousDebugWriteDurationMs,
    details: input.details ? (sanitizeClientDebugValue(input.details) as Record<string, unknown>) : undefined,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugSliderTrailEntry>(REPORT_DEBUG_SLIDER_TRAIL_KEY),
    sanitizeClientDebugValue(nextItem) as ReportDebugSliderTrailEntry,
  ].slice(-REPORT_DEBUG_SLIDER_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_SLIDER_TRAIL_KEY, next);
  const finishedAtMs = typeof performance !== "undefined" ? performance.now() : Date.now();
  return Math.max(0, Math.round(finishedAtMs - startedAtMs));
}

export function readReportDebugSliderTrail(
  limit = REPORT_DEBUG_SLIDER_TRAIL_MAX
): ReportDebugSliderTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugSliderTrailEntry>(REPORT_DEBUG_SLIDER_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function appendReportDebugProgramSelectorDragTrailEvent(
  input: Omit<ReportDebugProgramSelectorDragTrailEntry, "atIso" | "routePath" | "source"> & {
    routePath?: string | null;
  }
): number | null {
  if (typeof window === "undefined") return null;
  const startedAtMs = typeof performance !== "undefined" ? performance.now() : Date.now();
  const nextItem: ReportDebugProgramSelectorDragTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    source: "program-selector-drag",
    event: input.event,
    surface: input.surface,
    selectionKey: input.selectionKey,
    gestureId: input.gestureId,
    inputMode: input.inputMode,
    locked: input.locked,
    revealProgress: input.revealProgress,
    pullDistance: input.pullDistance,
    lockedScrollTop: input.lockedScrollTop,
    scrollTop: input.scrollTop,
    deltaY: input.deltaY,
    currentY: input.currentY,
    previousY: input.previousY,
    touchStartY: input.touchStartY,
    touchMoveCount: input.touchMoveCount,
    wheelEventCount: input.wheelEventCount,
    cancelable: input.cancelable,
    defaultPrevented: input.defaultPrevented,
    preventedCount: input.preventedCount,
    cancelableMoveCount: input.cancelableMoveCount,
    nonCancelableMoveCount: input.nonCancelableMoveCount,
    attemptedScrollTop: input.attemptedScrollTop,
    clampDeltaPx: input.clampDeltaPx,
    clampReason: input.clampReason,
    clampBurstId: input.clampBurstId,
    clampCount: input.clampCount,
    burstDurationMs: input.burstDurationMs,
    maxClampDeltaPx: input.maxClampDeltaPx,
    avgClampIntervalMs: input.avgClampIntervalMs,
    startedAfterRevealMs: input.startedAfterRevealMs,
    settleDurationMs: input.settleDurationMs,
    settleFrameCount: input.settleFrameCount,
    finalScrollTop: input.finalScrollTop,
    maxScrollTop: input.maxScrollTop,
    maxScrollPastLockedPx: input.maxScrollPastLockedPx,
    reboundPastLocked: input.reboundPastLocked,
    sincePreviousEventMs: input.sincePreviousEventMs,
    handlerDurationMs: input.handlerDurationMs,
    frameCount: input.frameCount,
    maxFrameGapMs: input.maxFrameGapMs,
    longFrameCount: input.longFrameCount,
    approxFps: input.approxFps,
    previousDebugWriteDurationMs: input.previousDebugWriteDurationMs,
    details: input.details ? (sanitizeClientDebugValue(input.details) as Record<string, unknown>) : undefined,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugProgramSelectorDragTrailEntry>(
      REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_KEY
    ),
    sanitizeClientDebugValue(nextItem) as ReportDebugProgramSelectorDragTrailEntry,
  ].slice(-REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_KEY, next);
  const finishedAtMs = typeof performance !== "undefined" ? performance.now() : Date.now();
  return Math.max(0, Math.round(finishedAtMs - startedAtMs));
}

export function readReportDebugProgramSelectorDragTrail(
  limit = REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_MAX
): ReportDebugProgramSelectorDragTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugProgramSelectorDragTrailEntry>(
    REPORT_DEBUG_PROGRAM_SELECTOR_DRAG_TRAIL_KEY
  ).slice(Math.max(0, -limit));
}

export function appendReportDebugGoalsEditTrailEvent(input: Omit<ReportDebugGoalsEditTrailEntry, "atIso" | "routePath"> & {
  routePath?: string | null;
}): void {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugGoalsEditTrailEntry = {
    atIso: new Date().toISOString(),
    routePath:
      input.routePath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
    source: input.source,
    event: input.event,
    goalId: input.goalId,
    activeGoalId: input.activeGoalId,
    isEditing: input.isEditing,
    field: input.field,
    draftValue: input.draftValue,
    parsedValue: input.parsedValue,
    currentValue: input.currentValue,
    maintenanceWeeks: input.maintenanceWeeks,
    maintenanceWeeksRemaining: input.maintenanceWeeksRemaining,
    reason: input.reason,
    requestId: input.requestId,
    success: input.success,
    error: input.error,
  };
  const next = [
    ...readPersistedSessionArray<ReportDebugGoalsEditTrailEntry>(REPORT_DEBUG_GOALS_EDIT_TRAIL_KEY),
    sanitizeClientDebugValue(nextItem) as ReportDebugGoalsEditTrailEntry,
  ].slice(-REPORT_DEBUG_GOALS_EDIT_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_GOALS_EDIT_TRAIL_KEY, next);
}

export function readReportDebugGoalsEditTrail(
  limit = REPORT_DEBUG_GOALS_EDIT_TRAIL_MAX
): ReportDebugGoalsEditTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugGoalsEditTrailEntry>(REPORT_DEBUG_GOALS_EDIT_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function appendOnboardingEvent(input: {
  source: string;
  event: string;
  stepId?: string;
  details?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ClientDebugAction = {
    atIso: new Date().toISOString(),
    source: input.source,
    event: input.event,
    details: sanitizeClientDebugValue({
      stepId: input.stepId ?? null,
      ...(input.details || {}),
    }) as Record<string, unknown>,
  };
  const next = [
    ...readPersistedSessionArray<ClientDebugAction>(REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_KEY),
    nextItem,
  ].slice(-REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_MAX);
  writePersistedSessionJson(REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_KEY, next);
}

export function readOnboardingEventTrail(limit = REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_MAX): ClientDebugAction[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ClientDebugAction>(REPORT_DEBUG_ONBOARDING_EVENT_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function appendReportDebugWorkoutTrailEvent(input: {
  source: string;
  event: string;
  routePath?: string | null;
  sessionId?: string | null;
  programDayId?: string | null;
  clientProgramId?: string | null;
  details?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  const nextItem: ReportDebugWorkoutTrailEntry = {
    atIso: new Date().toISOString(),
    source: input.source,
    event: input.event,
    routePath: input.routePath ?? null,
    sessionId: input.sessionId ?? null,
    programDayId: input.programDayId ?? null,
    clientProgramId: input.clientProgramId ?? null,
    details: input.details ? (sanitizeClientDebugValue(input.details) as Record<string, unknown>) : undefined,
  };
  const next = [...readPersistedSessionArray<ReportDebugWorkoutTrailEntry>(REPORT_DEBUG_WORKOUT_TRAIL_KEY), nextItem].slice(
    -REPORT_DEBUG_WORKOUT_TRAIL_MAX
  );
  writePersistedSessionJson(REPORT_DEBUG_WORKOUT_TRAIL_KEY, next);
}

export function clearReportDebugWorkoutState(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(REPORT_DEBUG_WORKOUT_STATE_KEY);
  window.sessionStorage.removeItem(REPORT_DEBUG_WORKOUT_TRAIL_KEY);
}

export function readReportDebugWorkoutTrail(limit = REPORT_DEBUG_WORKOUT_TRAIL_MAX): ReportDebugWorkoutTrailEntry[] {
  if (limit <= 0) return [];
  return readPersistedSessionArray<ReportDebugWorkoutTrailEntry>(REPORT_DEBUG_WORKOUT_TRAIL_KEY).slice(
    Math.max(0, -limit)
  );
}

export function persistReportDebugStartWorkoutBreadcrumb(
  breadcrumb: ReportDebugStartWorkoutBreadcrumb | null
): void {
  if (typeof window === "undefined") return;
  if (!breadcrumb) {
    window.sessionStorage.removeItem(REPORT_DEBUG_START_WORKOUT_BREADCRUMB_KEY);
    return;
  }
  writePersistedSessionJson(REPORT_DEBUG_START_WORKOUT_BREADCRUMB_KEY, breadcrumb);
}

export function readReportDebugStartWorkoutBreadcrumb(): ReportDebugStartWorkoutBreadcrumb | null {
  return readPersistedSessionJson<ReportDebugStartWorkoutBreadcrumb>(REPORT_DEBUG_START_WORKOUT_BREADCRUMB_KEY);
}

export function appendClientDebugAction(input: {
  source: string;
  event: string;
  details?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  if (!shouldCollectClientDebugActions()) return;
  const nextItem: ClientDebugAction = {
    atIso: new Date().toISOString(),
    source: input.source,
    event: input.event,
    details: input.details,
  };
  try {
    const existingRaw = localStorage.getItem(CLIENT_DEBUG_ACTIONS_KEY);
    const existing = (safeParseJson(existingRaw) as ClientDebugAction[] | null) || [];
    const next = [...existing, nextItem].slice(-CLIENT_DEBUG_ACTIONS_MAX);
    localStorage.setItem(CLIENT_DEBUG_ACTIONS_KEY, JSON.stringify(next));
  } catch {
    // No-op: diagnostics must never block product flow.
  }
  if (input.source === "keyboard_debug") {
    writePersistedSessionJson(CLIENT_KEYBOARD_DEBUG_SNAPSHOT_KEY, sanitizeClientDebugAction(nextItem));
  }
  window.dispatchEvent(new CustomEvent(CLIENT_DEBUG_ACTION_EVENT, { detail: nextItem }));
}

export function readClientKeyboardDebugSnapshot(): ClientDebugAction | null {
  return readPersistedSessionJson<ClientDebugAction>(CLIENT_KEYBOARD_DEBUG_SNAPSHOT_KEY);
}

export function readClientDebugActions(limit = 50): ClientDebugAction[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = safeParseJson(localStorage.getItem(CLIENT_DEBUG_ACTIONS_KEY));
    if (!Array.isArray(parsed)) return [];
    const actions = parsed as ClientDebugAction[];
    if (limit <= 0) return [];
    return actions.slice(Math.max(0, actions.length - limit));
  } catch {
    return [];
  }
}

/**
 * Scans localStorage for onboarding_seen_steps:* keys. Used as a fallback
 * when the dedicated onboardingTipDebug key is missing from localStorage.
 */
function scanOnboardingStorageFallback(): {
  found: boolean;
  scopeKeys: string[];
  stepCount: number;
  stepIds: string[];
} {
  if (typeof window === "undefined") return { found: false, scopeKeys: [], stepCount: 0, stepIds: [] };
  const scopeKeys: string[] = [];
  const allStepIds: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key?.startsWith("onboarding_seen_steps:")) continue;
    scopeKeys.push(key);
    try {
      const value = JSON.parse(window.localStorage.getItem(key) ?? "[]");
      if (Array.isArray(value)) {
        for (const step of value) {
          if (typeof step === "string") allStepIds.push(step);
        }
      }
    } catch {
      // ignore parse errors
    }
  }
  return {
    found: scopeKeys.length > 0,
    scopeKeys,
    stepCount: allStepIds.length,
    stepIds: allStepIds,
  };
}

export function buildClientDebugSnapshot(input: BuildDebugSnapshotInput): ClientDebugSnapshot {
  if (typeof window === "undefined") {
    return {
      userNote: input.userNote || "",
      snapshotAtIso: new Date().toISOString(),
      runtime: "server",
      location: {},
      env: {},
      routeHistory: [] as unknown[],
      navTrail: [] as DebugTimelineEntry[],
      historyTrail: [] as DebugTimelineEntry[],
      lifecycleTrail: [] as DebugTimelineEntry[],
    };
  }

  const pendingSyncRaw = safeParseJson(localStorage.getItem(getScopedStorageKey("pendingSyncOperations")));
  const pendingSyncState = buildPendingSyncDebugState(pendingSyncRaw);
  const workoutChangeSaveTelemetry = safeParseJson(localStorage.getItem("workoutChangeSaveTelemetry"));
  const workoutRestTimerDebug = safeParseJson(localStorage.getItem("workoutRestTimerDebug"));
  const workoutStoreSummary = summarizeWorkoutStore(loadWorkoutFromStore());
  const workoutReportDebugState = summarizeWorkoutDebugState(readReportDebugWorkoutState());
  const workoutReportDebugTrail = readReportDebugWorkoutTrail();
  const startWorkoutBreadcrumb = readReportDebugStartWorkoutBreadcrumb();
  const latestOnboardingTipDebug = readReportDebugOnboardingTipState();
  const onboardingStepsFallback = latestOnboardingTipDebug == null ? scanOnboardingStorageFallback() : null;
  const onboardingEventTrail = readOnboardingEventTrail();
  const latestPullToRefreshDebugState = readReportDebugPullToRefreshState();
  const latestGalleryDataDebugState = readReportDebugGalleryDataState();
  const galleryCapturedDateTrail = readReportDebugGalleryCapturedDateTrail();
  const latestGalleryUploadProcessingDebugState = readReportDebugGalleryUploadProcessingState();
  const latestVideoUploadDebugState = readReportDebugVideoUploadState();
  const mediaUploadPerfTrail = readReportDebugMediaUploadPerfTrail();
  const galleryPreviewPerfTrail = readReportDebugGalleryPreviewPerfTrail();
  const latestDashboardOverviewChartDebugState = readReportDebugDashboardOverviewChartState();
  const dashboardOverviewChartDebugTrail = readReportDebugDashboardOverviewChartTrail();
  const latestDashboardDailyCaloriesDebugState = readReportDebugDashboardDailyCaloriesState();
  const dashboardDailyCaloriesDebugTrail = readReportDebugDashboardDailyCaloriesTrail();
  const pullToRefreshDebugTrail = readReportDebugPullToRefreshTrail();
  const latestHistoryQueryDebugState = readReportDebugHistoryQueryState();
  const nutritionCustomFoodTrail = readReportDebugNutritionCustomFoodTrail();
  const nutritionSearchTrail = readReportDebugNutritionSearchTrail();
  const nutritionLogTrail = readReportDebugNutritionLogTrail();
  const dailyRoutineTrail = readReportDebugDailyRoutineTrail();
  const sliderDebugTrail = readReportDebugSliderTrail();
  const programSelectorDragTrail = readReportDebugProgramSelectorDragTrail();
  const goalsEditTrail = readReportDebugGoalsEditTrail();
  const latestUiCardSnapshots = readReportDebugUiCardSnapshots();
  const reportDebugUiCardSnapshots = collectReportDebugUiCardSnapshots();
  const uiCardDebugTrail = readReportDebugUiCardTrail();
  const latestHistoryEditNavigationDebugSnapshot = readReportDebugHistoryEditNavigationSnapshot();
  const latestHistoryEditScrollDebugSnapshot = readReportDebugHistoryEditScrollSnapshot();
  const sanitizedDashboardOverviewChartDebugState = latestDashboardOverviewChartDebugState
    ? sanitizeClientDebugValue(latestDashboardOverviewChartDebugState, 0, 6)
    : null;
  const sanitizedDashboardOverviewChartDebugTrail = sanitizeClientDebugValue(
    dashboardOverviewChartDebugTrail,
    0,
    6
  );
  const sanitizedDashboardDailyCaloriesDebugState = latestDashboardDailyCaloriesDebugState
    ? sanitizeClientDebugValue(latestDashboardDailyCaloriesDebugState, 0, 6)
    : null;
  const sanitizedDashboardDailyCaloriesDebugTrail = sanitizeClientDebugValue(
    dashboardDailyCaloriesDebugTrail,
    0,
    6
  );
  const clientNavTransactionTrail = readClientNavTransactions(10).map((transaction) =>
    sanitizeClientDebugValue(transaction)
  );
  const latestKeyboardDebugSnapshot = readClientKeyboardDebugSnapshot();
  const rawRecentActions = readClientDebugActions(SNAPSHOT_RECENT_ACTIONS_MAX);
  const recentActions = rawRecentActions.map((action) => sanitizeClientDebugAction(action));
  const startWorkoutTrace = recentActions
    .filter((action) => action.event.startsWith("start-workout-"))
    .slice(-SNAPSHOT_START_WORKOUT_TRACE_MAX);
  const startClickAction = startWorkoutTrace.find((action) => {
    const details = action.details || {};
    return action.event === "start-workout-phase" && details.phase === "start-clicked";
  });
  const routerPushAction = startWorkoutTrace.find((action) => {
    const details = action.details || {};
    return action.event === "start-workout-phase" && details.phase === "router-push-called";
  });
  const sessionMountedAction = startWorkoutTrace.find((action) => {
    const details = action.details || {};
    return action.event === "start-workout-arrival" && details.phase === "session-component-mounted";
  });
  const startClickAtMs = toMs(startClickAction?.atIso);
  const routerPushAtMs = toMs(routerPushAction?.atIso);
  const sessionMountedAtMs = toMs(sessionMountedAction?.atIso);
  const startWorkoutSummary = {
    startClickedAt: startClickAction?.atIso ?? null,
    routerPushAt: routerPushAction?.atIso ?? null,
    workoutMountedAt: sessionMountedAction?.atIso ?? null,
    startToPushMs:
      startClickAtMs !== null && routerPushAtMs !== null ? Math.max(0, routerPushAtMs - startClickAtMs) : null,
    pushToMountedMs:
      routerPushAtMs !== null && sessionMountedAtMs !== null ? Math.max(0, sessionMountedAtMs - routerPushAtMs) : null,
    startToMountedMs:
      startClickAtMs !== null && sessionMountedAtMs !== null ? Math.max(0, sessionMountedAtMs - startClickAtMs) : null,
    breadcrumbCapturedAt: startWorkoutBreadcrumb?.capturedAtIso ?? null,
    breadcrumbSource: startWorkoutBreadcrumb?.source ?? null,
    breadcrumbSourceComponent: startWorkoutBreadcrumb?.sourceComponent ?? null,
    breadcrumbRoutePath: startWorkoutBreadcrumb?.routePath ?? null,
    breadcrumbSessionId: startWorkoutBreadcrumb?.sessionId ?? null,
    breadcrumbRoutineId: startWorkoutBreadcrumb?.routineId ?? null,
    breadcrumbClientProgramId: startWorkoutBreadcrumb?.clientProgramId ?? null,
    breadcrumbStartClickedAt:
      startWorkoutBreadcrumb !== null ? new Date(startWorkoutBreadcrumb.startClickedAtMs).toISOString() : null,
    breadcrumbPushCalledAt:
      startWorkoutBreadcrumb !== null ? new Date(startWorkoutBreadcrumb.pushCalledAtMs).toISOString() : null,
  };

  const location = {
    href: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    historyLength: window.history.length,
  };

  const env = {
    visibility: typeof document !== "undefined" ? document.visibilityState : "unknown",
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: truncateString(window.navigator.userAgent || "unknown", 300),
    language: window.navigator.language,
    online: window.navigator.onLine,
  };
  const sanitizedExtra = sanitizeClientDebugValue({
    ...(input.extra || {}),
    latestKeyboardDebugSnapshot,
    latestHistoryEditNavigationDebugSnapshot,
    latestHistoryEditScrollDebugSnapshot,
    latestPullToRefreshDebugState,
    latestGalleryDataDebugState,
    galleryCapturedDateTrail,
    latestGalleryUploadProcessingDebugState,
    latestVideoUploadDebugState,
    mediaUploadPerfTrail,
    galleryPreviewPerfTrail,
    pullToRefreshDebugTrail,
    latestHistoryQueryDebugState,
    nutritionCustomFoodTrail,
    nutritionSearchTrail,
    nutritionLogTrail,
    dailyRoutineTrail,
    sliderDebugTrail,
    programSelectorDragTrail,
    goalsEditTrail,
  }, 0, 5) as Record<string, unknown>;

  return {
    onboardingTipDebug: latestOnboardingTipDebug,
    onboardingStepsFallback,
    onboardingEventTrail,
    userNote: (input.userNote || "").trim().slice(0, 1500),
    snapshotAtIso: new Date().toISOString(),
    location,
    env,
    routeHistory: clampArray<unknown>(input.routeHistory, SNAPSHOT_ROUTE_HISTORY_MAX),
    navTrail: clampArray<DebugTimelineEntry>(input.navTrail, SNAPSHOT_TRAIL_MAX),
    historyTrail: clampArray<DebugTimelineEntry>(input.historyTrail, SNAPSHOT_TRAIL_MAX),
    lifecycleTrail: clampArray<DebugTimelineEntry>(input.lifecycleTrail, SNAPSHOT_TRAIL_MAX),
    pendingSync: pendingSyncState.pendingSync,
    pendingSyncCount: pendingSyncState.pendingSyncCount,
    queueHead: pendingSyncState.queueHead,
    trackedSessionIds: pendingSyncState.trackedSessionIds,
    workoutChangeSaveTelemetry: Array.isArray(workoutChangeSaveTelemetry)
      ? workoutChangeSaveTelemetry.slice(-20)
      : [],
    workoutRestTimerDebug:
      workoutRestTimerDebug && typeof workoutRestTimerDebug === "object"
        ? sanitizeClientDebugValue(workoutRestTimerDebug)
        : null,
    workoutStoreSummary,
    workoutReportDebugState,
    workoutReportDebugTrail,
    clientNavTransactionTrail,
    recentActions,
    startWorkoutTrace,
    startWorkoutSummary,
    extra: {
      ...sanitizedExtra,
      latestDashboardOverviewChartDebugState: sanitizedDashboardOverviewChartDebugState,
      dashboardOverviewChartDebugTrail: sanitizedDashboardOverviewChartDebugTrail,
      latestDashboardDailyCaloriesDebugState: sanitizedDashboardDailyCaloriesDebugState,
      dashboardDailyCaloriesDebugTrail: sanitizedDashboardDailyCaloriesDebugTrail,
      latestUiCardSnapshots,
      reportDebugUiCardSnapshots,
      uiCardDebugTrail,
    },
  };
}
